import MockAdapter from 'axios-mock-adapter'
import moment from 'moment-timezone'

import {AutofillStatus, OptimizerRunStatus, PlannerForecastType} from '../../shared/enums'
import {
  OptimizerRunResponse,
  OptimizerSettingsDTO,
  RecalculatePlanDTO
} from '../../shared/interfaces/api/optimizer'
import {mockStore, TEST_PLANT_CODE} from '../mockStore'

import {saveScenario, sleepResponse, numberRegEx} from './utils'
export const enableDSEndpoints = (mock: MockAdapter) => {
  // Plant-access validation endpoint; returns 200 for the valid test plant.
  mock.onGet(`/hcem/ui/v2/latest/${TEST_PLANT_CODE}`).reply(() => sleepResponse([200, {}]))

  // GET Electricity prices (Py)
  mock.onGet(`/hcem/ui/electricity/${TEST_PLANT_CODE}`).reply((config) => {
    const {time_from: from, time_to: to} = config.params as {time_from: string; time_to: string}
    const {
      burglengenfeld: {electricityPrice}
    } = mockStore.scenario()

    const filteredActuals = electricityPrice.actual.filter((entry) =>
      moment.utc(entry.datetime).isBetween(from, to, 'second', '[)')
    )
    const filteredForecast = electricityPrice.forecast.filter((entry) =>
      moment.utc(entry.datetime).isBetween(from, to, 'second', '[)')
    )

    return sleepResponse([
      200,
      {...electricityPrice, actual: filteredActuals, forecast: filteredForecast}
    ])
  })

  // GET optimizer runs (Py)
  mock.onGet(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/runs`).reply(() => {
    const {burglengenfeld} = mockStore.scenario()
    return sleepResponse([200, burglengenfeld.optimizerRuns])
  })

  mock.onGet(`/hcem/ui/peak_load_windows/${TEST_PLANT_CODE}`).reply((config) => {
    const {time_from: from, time_to: to} = config.params as {time_from: string; time_to: string}
    const {
      burglengenfeld: {peakLoadWindows, backendErrors}
    } = mockStore.scenario()
    const filteredPeakLoadWindows = peakLoadWindows.filter(
      (entry) => moment.utc(entry.start).isBefore(to) && moment.utc(entry.end).isAfter(from)
    )
    if (backendErrors.peakLoadWindowsError) {
      return sleepResponse(backendErrors.peakLoadWindowsError)
    }
    return sleepResponse([200, filteredPeakLoadWindows])
  })

  function checkAutofillExpiring() {
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {autofillStatus}
    } = scenario

    if (autofillStatus.data.status === AutofillStatus.InProgress) {
      if (!autofillStatus.inProgressChange) {
        throw new Error('In progress change must be defined')
      }

      // in progress autofill expires after 1minute
      if (moment(autofillStatus.inProgressChange).isBefore(moment().subtract(1, 'minute'))) {
        scenario.burglengenfeld.autofillStatus.inProgressChange = moment().toISOString()
        scenario.burglengenfeld.autofillStatus.data.status = AutofillStatus.NotStarted
        scenario.burglengenfeld.autofillStatus.data.last_updated_at = moment().toISOString()
        saveScenario(scenario)
      }
    }
  }

  mock.onGet(`/hcem/ui/v2/autofill/${TEST_PLANT_CODE}/status`).reply(() => {
    checkAutofillExpiring()
    const {
      burglengenfeld: {
        autofillStatus: {data}
      }
    } = mockStore.scenario()
    return sleepResponse([200, data])
  })

  mock.onPost(`/hcem/ui/v2/autofill/${TEST_PLANT_CODE}`).reply(() => {
    checkAutofillExpiring()
    const scenario = mockStore.scenario()
    const {
      burglengenfeld: {autofillStatus}
    } = scenario

    if (autofillStatus.data.status === AutofillStatus.InProgress) {
      if (!autofillStatus.inProgressChange) {
        // TODO: check correct status with DS BE
        return sleepResponse([409, {message: 'Autofill already in progress'}])
      }
    }

    scenario.burglengenfeld.autofillStatus.inProgressChange = moment().toISOString()
    scenario.burglengenfeld.autofillStatus.data.status = AutofillStatus.InProgress
    scenario.burglengenfeld.autofillStatus.data.last_updated_at = moment().toISOString()
    saveScenario(scenario)

    return sleepResponse([202, autofillStatus])
  })

  mock.onGet(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/settings`).reply(() => {
    const {
      burglengenfeld: {optimizerSettings}
    } = mockStore.scenario()
    return sleepResponse([200, optimizerSettings])
  })

  mock.onPatch(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/settings`).reply(({data}) => {
    const dto = JSON.parse(data as string) as OptimizerSettingsDTO
    const scenario = mockStore.scenario()
    scenario.burglengenfeld.optimizerSettings = dto
    saveScenario(scenario)
    return sleepResponse([200, {}])
  })

  mock.onPost(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/runs`).reply(({data}) => {
    const dto = JSON.parse(data as string) as RecalculatePlanDTO
    const scenario = mockStore.scenario()

    // Simulate adding a new optimizer run
    const newRun: OptimizerRunResponse = {
      name: dto.name,
      forecast_type: dto.forecast_type || 'ds_forecast',
      status: OptimizerRunStatus.InProgress,
      last_updated_at: moment.utc().toISOString(),
      queued_at: moment.utc().toISOString(),
      queued_by: 'Test User',
      optimizer_run_id: Date.now()
    }

    if (dto.save_settings === true) {
      scenario.burglengenfeld.optimizerSettings = {
        ...dto.settings,
        optimize_from: moment(dto.settings.optimize_from).format('HH:mm')
      }
    }

    scenario.burglengenfeld.optimizerRuns.push(newRun)
    saveScenario(scenario)

    return sleepResponse([202, newRun])
  })

  mock
    .onDelete(new RegExp(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/runs/${numberRegEx}`))
    .reply((config) => {
      const runId = parseInt(config.url!.split('/')[7])
      const scenario = mockStore.scenario()

      const runs = scenario.burglengenfeld.optimizerRuns.map((run) => {
        if (run.optimizer_run_id === runId) {
          return {...run, status: OptimizerRunStatus.Cancelled}
        }
        return run
      })
      scenario.burglengenfeld.optimizerRuns = runs
      saveScenario(scenario)

      return sleepResponse([200, {}])
    })

  mock.onPost(new RegExp(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/run-analysis`)).reply(() => {
    const scenario = mockStore.scenario()

    // Simulate adding a new optimizer run
    const analyzedProductionPlanRun: OptimizerRunResponse = {
      name: 'Analysis of current plan',
      forecast_type: PlannerForecastType.StatisticalForecast,
      status: OptimizerRunStatus.InProgress,
      last_updated_at: moment.utc().toISOString(),
      queued_at: moment.utc().toISOString(),
      queued_by: 'Test User',
      optimizer_run_id: Date.now()
    }

    scenario.burglengenfeld.optimizerRuns.push(analyzedProductionPlanRun)
    saveScenario(scenario)

    return sleepResponse([202, null])
  })

  mock.onGet(new RegExp(`/hcem/ui/v2/optimizer/${TEST_PLANT_CODE}/constraints`)).reply(() => {
    const {
      burglengenfeld: {optimizerConstraints}
    } = mockStore.scenario()

    return sleepResponse([200, optimizerConstraints])
  })

  return mock
}
